import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Loader2, RefreshCw, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { StatusChip } from "@/components/common/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Fix P (2026-09-29): panel de integraciones. Muestra qué está conectado y qué falta (solo los
// NOMBRES de las variables, nunca los valores), las URLs exactas para pegar en Twilio, SendGrid
// e Intuit, y permite mandar un mensaje de prueba por un canal que ya esté en vivo.

const BRAND_LABEL = { bb: "B&B", betos: "Betos" };
// Fix W (2026-10-19): estados de la sesión de WhatsApp que informa Wasender.
const SESION_WASENDER = ["connected", "connecting", "need_scan", "need_passkey", "disconnected", "logged_out", "expired"];

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-[8px] border border-line bg-white p-4">
    <h2 className="mb-3 text-[13px] font-semibold text-ink">{title}</h2>
    {children}
  </section>
);

const Row = ({ label, children, testId }) => (
  <div data-testid={testId} className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-2 last:border-0">
    <span className="text-[12.5px] text-ink-secondary">{label}</span>
    <span className="flex flex-wrap items-center gap-1.5 text-right text-[12.5px] text-ink">{children}</span>
  </div>
);

const Ok = ({ ok, yes, no, testId }) => (
  <StatusChip testId={testId} tone={ok ? "green" : "amber"} label={ok ? yes : no} />
);

export const IntegrationsTab = () => {
  const { t } = useT();
  const [canal, setCanal] = useState("");
  const [destino, setDestino] = useState("");
  const [enviando, setEnviando] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["integrations-status"],
    queryFn: async () => (await api.get("/integrations/status")).data,
  });

  if (isLoading || !data) {
    return <p className="text-[13px] text-ink-tertiary">{t("app.loading")}</p>;
  }

  const base = data.public_base_url || "";
  const baseOk = base.startsWith("https://") && !base.includes("preview") && !base.includes("localhost");
  const msj = data.mensajeria;
  const enVivo = msj.canales.filter((c) => c.en_vivo && !c.faltan.length).map((c) => c.canal);
  const qbo = data.quickbooks;

  const copiar = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("settings.integrations.copied"));
    } catch {
      toast.error(url);
    }
  };

  const probar = async () => {
    setEnviando(true);
    try {
      const { data: res } = await api.post("/integrations/test", { canal, destino });
      if (res.ok) toast.success(t("settings.integrations.test_sent"));
      else toast.error(res.error || t("app.error"));
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-2" data-testid="integrations-tab">
      <div className="space-y-4">
        <Section title={t("settings.integrations.general")} testId="integrations-general">
          <Row label="PUBLIC_BASE_URL" testId="integrations-base-url">
            <span className="font-mono text-[12px]">{base || "—"}</span>
            <Ok ok={baseOk} yes={t("settings.integrations.ok")} no={t("settings.integrations.check")} />
          </Row>
          <Row label={t("settings.integrations.storage")} testId="integrations-storage">
            <Ok ok={data.almacenamiento.persistente}
              yes={data.almacenamiento.backend === "mongo"
                ? t("settings.integrations.storage_db")
                : data.almacenamiento.backend.toUpperCase()}
              no={t("settings.integrations.storage_local")} />
          </Row>
          {!data.almacenamiento.persistente && (
            <p className="mt-1 text-[11.5px] text-ink-tertiary">{t("settings.integrations.storage_hint")}</p>
          )}
          <Row label={t("settings.integrations.pilot")} testId="integrations-pilot">
            {data.piloto.modo_piloto
              ? t("settings.integrations.pilot_on", { n: data.piloto.edificios })
              : t("settings.integrations.pilot_off")}
          </Row>
        </Section>

        <Section title={t("settings.integrations.messaging")} testId="integrations-messaging">
          <Row label={t("settings.integrations.mode")}>
            <StatusChip tone={msj.modo === "real" ? "green" : "gray"}
              label={msj.modo === "real" ? t("settings.integrations.mode_real") : t("settings.integrations.mode_sim")} />
          </Row>
          {msj.canales.map((c) => (
            <Row key={c.canal} label={t(`channels.${c.canal}`)} testId={`integrations-channel-${c.canal}`}>
              {c.faltan.length > 0 && (
                <span className="font-mono text-[11px] text-state-warn">
                  {t("settings.integrations.missing", { v: c.faltan.join(", ") })}
                </span>
              )}
              <StatusChip
                tone={c.en_vivo && !c.faltan.length ? "green" : c.en_vivo ? "red" : "gray"}
                label={c.en_vivo && !c.faltan.length
                  ? t("settings.integrations.live")
                  : c.en_vivo ? t("settings.integrations.incomplete")
                  : msj.modo === "real" ? t("settings.integrations.not_connected")
                  : t("settings.integrations.simulated")} />
            </Row>
          ))}
          <Row label={t("settings.integrations.twilio_signature")}>
            <Ok ok={msj.firma_twilio} yes={t("settings.integrations.active")} no={t("settings.integrations.inactive")} />
          </Row>
          <Row label={t("settings.integrations.wa_templates")} testId="integrations-wa-templates">
            <Ok ok={!msj.whatsapp_plantillas.sin_content_sid.length}
              yes={t("settings.integrations.all_approved")}
              no={t("settings.integrations.without_sid", { n: msj.whatsapp_plantillas.sin_content_sid.length })} />
          </Row>
          {msj.whatsapp_plantillas.sin_content_sid.length > 0 && (
            <p className="mt-1 text-[11.5px] text-ink-tertiary">
              {msj.whatsapp_plantillas.sin_content_sid.map((e) => t(`messages.events.${e}`)).join(" · ")}
            </p>
          )}
          <Row label={t("settings.integrations.email_signature")}>
            <Ok ok={msj.email_entrante_firma} yes={t("settings.integrations.active")} no={t("settings.integrations.inactive")} />
          </Row>
          {/* Fix S (2026-10-01): email entrante por Resend */}
          <Row label={t("settings.integrations.resend_inbound")} testId="integrations-resend">
            <Ok ok={!!msj.email_entrante_resend} yes={t("settings.integrations.active")}
              no={t("settings.integrations.missing", { v: "RESEND_API_KEY" })} />
            {msj.email_entrante_resend && (
              <Ok ok={!!msj.email_entrante_resend_firma} yes={t("settings.integrations.signed")}
                no={t("settings.integrations.unsigned")} />
            )}
          </Row>
          {/* Fix W (2026-10-19): WhatsApp por Wasender */}
          {msj.whatsapp_proveedor === "wasender" && (
            <Row label={t("settings.integrations.wasender")} testId="integrations-wasender">
              <Ok ok={!!msj.wasender?.api_key} yes={t("settings.integrations.active")}
                no={t("settings.integrations.missing", { v: "WASENDER_API_KEY" })} />
              <Ok ok={!!msj.wasender?.firma} yes={t("settings.integrations.signed")}
                no={t("settings.integrations.missing", { v: "WASENDER_WEBHOOK_SECRET" })} />
              <StatusChip testId="integrations-wasender-session"
                tone={msj.wasender?.sesion === "connected" ? "green" : msj.wasender?.sesion ? "red" : "gray"}
                label={t(`settings.integrations.wasender_states.${SESION_WASENDER.includes(msj.wasender?.sesion) ? msj.wasender.sesion : "unknown"}`)} />
            </Row>
          )}
          <Row label="EMAIL_REPLY_TO">
            <span className="font-mono text-[12px]">{msj.email_reply_to || t("settings.integrations.brand_email")}</span>
          </Row>
        </Section>

        <Section title={t("settings.integrations.test_title")} testId="integrations-test">
          <p className="mb-2 text-[12px] text-ink-tertiary">{t("settings.integrations.test_desc")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[12px] text-ink-secondary">{t("settings.integrations.channel")}</Label>
              <Select value={canal} onValueChange={setCanal} disabled={!enVivo.length}>
                <SelectTrigger data-testid="integrations-test-channel" className="mt-1 h-8 w-[150px] rounded-[6px] text-[12.5px]">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {enVivo.map((c) => (
                    <SelectItem key={c} value={c}>{t(`channels.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px] flex-1">
              <Label className="text-[12px] text-ink-secondary">{t("settings.integrations.destination")}</Label>
              <Input data-testid="integrations-test-destination" value={destino}
                onChange={(e) => setDestino(e.target.value)}
                placeholder={canal === "email" ? "nombre@empresa.com" : "(415) 555-0123"}
                className="mt-1 h-8 rounded-[6px] text-[12.5px]" />
            </div>
            <Button data-testid="integrations-test-send" disabled={!canal || !destino.trim() || enviando}
              onClick={probar} className="h-8 rounded-[6px] bg-brand text-[12.5px] hover:bg-brand-hover">
              {enviando ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              {t("settings.integrations.test_send")}
            </Button>
          </div>
          {!enVivo.length && (
            <p className="mt-2 text-[11.5px] text-ink-tertiary">{t("settings.integrations.test_none")}</p>
          )}
          {canal === "whatsapp" && msj.whatsapp_proveedor !== "wasender" && (
            <p className="mt-2 text-[11.5px] text-ink-tertiary">{t("settings.integrations.test_whatsapp")}</p>
          )}
        </Section>
      </div>

      <div className="space-y-4">
        <Section title={t("settings.integrations.webhooks")} testId="integrations-webhooks">
          <p className="mb-2 text-[12px] text-ink-tertiary">{t("settings.integrations.webhooks_desc")}</p>
          {data.webhooks.map((w) => (
            <div key={w.clave} data-testid={`integrations-webhook-${w.clave}`} className="border-b border-line py-2 last:border-0">
              <p className="text-[12px] font-medium text-ink">{t(`settings.integrations.hooks.${w.clave}`)}</p>
              <div className="mt-1 flex items-center gap-1.5">
                <code className="min-w-0 flex-1 truncate rounded-[5px] bg-surface-sunken px-1.5 py-1 font-mono text-[11.5px] text-ink-secondary">
                  {w.url || t("settings.integrations.no_base")}
                </code>
                {w.url && (
                  <Button variant="outline" data-testid={`integrations-copy-${w.clave}`} onClick={() => copiar(w.url)}
                    className="h-7 rounded-[6px] px-2 text-[11.5px]">
                    <Copy className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Section>

        <Section title="QuickBooks" testId="integrations-qbo">
          <Row label={t("settings.integrations.mode")}>
            <StatusChip tone={qbo.proveedor === "intuit" ? "green" : "gray"}
              label={qbo.proveedor === "intuit"
                ? t("settings.integrations.qbo_real", { env: qbo.entorno })
                : t("settings.integrations.mode_sim")} />
          </Row>
          <Row label={t("settings.integrations.qbo_keys")}>
            <Ok ok={qbo.credenciales} yes={t("settings.integrations.ok")} no={t("settings.integrations.missing_short")} />
          </Row>
          <Row label="QBO_REDIRECT_URI" testId="integrations-qbo-redirect">
            <Ok ok={qbo.redirect_ok} yes={t("settings.integrations.ok")}
              no={qbo.redirect_uri ? t("settings.integrations.differs") : t("settings.integrations.missing_short")} />
          </Row>
          {qbo.conexiones.map((c) => (
            <Row key={c.brand} label={BRAND_LABEL[c.brand] || c.brand} testId={`integrations-qbo-${c.brand}`}>
              <span className="text-[12px] text-ink-tertiary">{c.company_name || ""}</span>
              <StatusChip tone={c.estado === "conectado" ? (c.provider === "intuit" ? "green" : "gray") : c.estado === "error" ? "red" : "amber"}
                label={c.estado === "conectado"
                  ? (c.provider === "intuit" ? t("settings.integrations.connected_real") : t("settings.integrations.connected_sim"))
                  : t(`settings.accounting.states.${c.estado}`)} />
            </Row>
          ))}
          {qbo.facturas_simuladas > 0 && (
            <p className="mt-2 text-[11.5px] text-ink-tertiary">
              {t("settings.integrations.qbo_sim_invoices", { n: qbo.facturas_simuladas })}
            </p>
          )}
        </Section>

        <Button variant="outline" data-testid="integrations-refresh" onClick={() => refetch()}
          className="h-8 rounded-[6px] text-[12.5px]">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          {t("settings.integrations.refresh")}
        </Button>
      </div>
    </div>
  );
};
